import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Button,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  LinearProgress,
  alpha,
  Stack,
  Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  AccountBalance as AccountBalanceIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as AttachMoneyIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import {
  caixaFinanceiroService,
  CaixaRegistro,
  CaixaResumoDashboard,
  MovimentacaoFilters
} from '../services/caixaFinanceiroService';
import CaixaFinanceiroForm from '../components/forms/CaixaFinanceiroForm';
import { formatarValor, formatarDataHora } from '../utils/formatToView';
import SysDateRangePicker from '../components/SysDateRangePicker';
import FormaPgtoSelect from '../components/selects/FormaPgtoSelect';
import GatewaySelect, { GATEWAY_FILTER_SEM_VALUE } from '../components/selects/GatewaySelect';
import UsuariosSelect from '../components/selects/UsuariosSelect';
import formaPgtoService from '../services/formaPgtoService';
import { getFormaPagamentoLabel } from '../utils/caixaFinanceiroUtils';
import { filterBarHeaderSx } from '../styles/theme';

interface KpiCardProps {
  titulo: string;
  badge?: string;
  valor: string | number;
  subtituloLabel: string;
  subtituloValor: string | number;
  icone: React.ReactNode;
  cor: string;
}

const KpiCard: React.FC<KpiCardProps> = ({
  titulo,
  badge,
  valor,
  subtituloLabel,
  subtituloValor,
  icone,
  cor,
}) => {
  const theme = useTheme();

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2.5,
        position: 'relative',
        overflow: 'hidden',
        border: `1px solid ${alpha(cor, 0.22)}`,
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(145deg, ${alpha(cor, 0.08)} 0%, ${alpha('#1e1e2f', 0.95)} 100%)`
            : `linear-gradient(145deg, ${alpha(cor, 0.05)} 0%, #ffffff 100%)`,
        transition: 'all 0.25s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 20px -6px ${alpha(cor, 0.22)}`,
          borderColor: alpha(cor, 0.45),
        },
      }}
    >
      {/* Barra superior com a cor temática */}
      <Box sx={{ height: 3.5, width: '100%', bgcolor: cor }} />

      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Cabeçalho do Card */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
          <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{
                fontSize: '0.68rem',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                color: 'text.secondary',
              }}
              noWrap
            >
              {titulo}
            </Typography>
            {badge && (
              <Chip
                label={badge}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  bgcolor: alpha(cor, 0.12),
                  color: cor,
                  borderRadius: 1,
                  px: 0.25,
                }}
              />
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: alpha(cor, 0.12),
              color: cor,
              width: 32,
              height: 32,
              boxShadow: `0 2px 6px ${alpha(cor, 0.18)}`,
              '& .MuiSvgIcon-root': { fontSize: 18 },
            }}
          >
            {icone}
          </Avatar>
        </Box>

        {/* Valor Principal */}
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{
            color: cor,
            fontSize: { xs: '1.05rem', sm: '1.2rem' },
            lineHeight: 1.2,
            mb: 1,
            letterSpacing: -0.2,
          }}
          noWrap
        >
          {valor}
        </Typography>

        {/* Pílula de Rodapé (Filtrado / Inicial) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            px: 1,
            py: 0.45,
            borderRadius: 1.5,
            bgcolor: alpha(cor, 0.07),
            border: `1px solid ${alpha(cor, 0.14)}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.68rem',
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {subtituloLabel}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: cor,
            }}
            noWrap
          >
            {subtituloValor}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

const dateToYmd = (d: Date | null): string => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { startDate: dateToYmd(first), endDate: dateToYmd(last) };
};

const parseYmdToLocalDate = (str?: string): Date | null => {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const CaixaFinanceiroDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const primario = theme.palette.primary.main;

  // Estados principais
  const [resumo, setResumo] = useState<CaixaResumoDashboard | null>(null);
  const [movimentacoes, setMovimentacoes] = useState<CaixaRegistro[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMovimentacoes, setLoadingMovimentacoes] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados de paginação para movimentações
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Estados de filtros para movimentações inicializados com o mês atual
  const [filters, setFilters] = useState<MovimentacaoFilters>(() => {
    const currentMonthRange = getCurrentMonthRange();
    return {
      page: 1,
      limit: 20,
      tipo: 'todos',
      data_inicio: currentMonthRange.startDate,
      data_fim: currentMonthRange.endDate,
    };
  });

  // Totais filtrados calculados pelo backend
  const [totaisFiltrados, setTotaisFiltrados] = useState<{
    total_entradas: number;
    total_saidas: number;
  }>({
    total_entradas: 0,
    total_saidas: 0
  });

  // Estados de modais e painéis
  const [editModal, setEditModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      tipo: 'todos',
      forma_pagamento: undefined,
      gateway_id: undefined,
      coletor_id: undefined,
      data_inicio: undefined,
      data_fim: undefined,
    });
  };

  const hasActiveFilters = Boolean(
    (filters.tipo && filters.tipo !== 'todos') ||
    filters.forma_pagamento ||
    (filters.gateway_id !== undefined && filters.gateway_id !== '') ||
    (filters.coletor_id !== undefined && filters.coletor_id !== '') ||
    filters.data_inicio ||
    filters.data_fim
  );

  const activeFiltersCount = [
    filters.tipo && filters.tipo !== 'todos',
    Boolean(filters.forma_pagamento),
    filters.gateway_id !== undefined && filters.gateway_id !== '',
    filters.coletor_id !== undefined && filters.coletor_id !== '',
    Boolean(filters.data_inicio || filters.data_fim)
  ].filter(Boolean).length;

  const [formasPgtoMap, setFormasPgtoMap] = useState<Record<string, string>>({});

  useEffect(() => {
    formaPgtoService.getFormasParaSelect()
      .then((formas) => {
        const map: Record<string, string> = {};
        formas.forEach((f) => {
          map[f.value] = f.label;
          map[f.value.toLowerCase()] = f.label;
        });
        setFormasPgtoMap(map);
      })
      .catch((err) => console.error('Erro ao carregar mapa de formas de pagamento:', err));
  }, []);

  const getFormaPagamentoNome = (codigo?: string) => {
    if (!codigo) return '-';
    // 1. Procurar no mapa dinâmico retornado pelo backend
    if (formasPgtoMap[codigo]) return formasPgtoMap[codigo];
    if (formasPgtoMap[codigo.toLowerCase()]) return formasPgtoMap[codigo.toLowerCase()];
    // 2. Procurar no utilitário de labels estáticos
    const label = getFormaPagamentoLabel(codigo.toLowerCase());
    if (label && label.toLowerCase() !== codigo.toLowerCase()) return label;
    // 3. Fallback formatado (remove underline e capitaliza)
    return codigo.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  useEffect(() => {
    if (id) {
      loadResumo();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadMovimentacoes();
    }
  }, [id, filters]);

  const loadResumo = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!id) throw new Error('ID do caixa não fornecido');

      console.log('🔍 Carregando resumo do caixa:', id);
      const response = await caixaFinanceiroService.getCaixaById(parseInt(id)) as any;

      if (response && response.success && response.data) {
        setResumo(response.data);
      } else {
        throw new Error('Caixa não encontrado ou resposta inválida');
      }
    } catch (err: any) {
      console.error('❌ Erro ao carregar resumo:', err);
      setError(err.message || 'Erro ao carregar dados do caixa');
    } finally {
      setLoading(false);
    }
  };

  const loadMovimentacoes = async () => {
    try {
      setLoadingMovimentacoes(true);

      if (!id) return;

      const response = await caixaFinanceiroService.getMovimentacoesCaixa(parseInt(id), filters) as any;

      if (response && response.success) {
        setMovimentacoes(response.data || []);
        if (response.pagination) {
          setPagination(response.pagination);
        }
        if (response.totais) {
          setTotaisFiltrados(response.totais);
        }
      } else {
        throw new Error('Erro ao carregar movimentações');
      }
    } catch (err: any) {
      console.error('❌ Erro ao carregar movimentações:', err);
      setError(err.message || 'Erro ao carregar movimentações');
    } finally {
      setLoadingMovimentacoes(false);
    }
  };

  const handleGoBack = () => {
    navigate('/caixa-financeiro');
  };

  const handleEditSuccess = () => {
    setEditModal(false);
    loadResumo();
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleFilterChange = (field: keyof MovimentacaoFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value === '' ? undefined : value,
      page: 1 // Reset para primeira página
    }));
  };

  const getTipoColor = (tipo: string) => {
    return tipo === 'entrada' ? 'success' : 'error';
  };

  const getTipoIcon = (tipo: string) => {
    return tipo === 'entrada' ? <TrendingUpIcon /> : <TrendingDownIcon />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: 1 }}>
        <CircularProgress size={32} />
        <Typography variant="caption" color="text.secondary">Carregando caixa...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>{error}</Alert>
        <Button variant="outlined" size="small" onClick={handleGoBack} startIcon={<ArrowBackIcon />}>Voltar</Button>
      </Box>
    );
  }

  if (!resumo) {
    return (
      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>Caixa não encontrado</Alert>
        <Button variant="outlined" size="small" onClick={handleGoBack} startIcon={<ArrowBackIcon />}>Voltar</Button>
      </Box>
    );
  }

  const caixa = resumo.caixa;
  const stats = resumo.estatisticas || {
    total_entradas_mes: 0,
    total_saidas_mes: 0,
    saldo_anterior_mes: 0,
    quantidade_movimentacoes_mes: 0,
    maior_entrada: 0,
    maior_saida: 0
  };

  const totalEntradasPagina = movimentacoes
    .filter(m => m.tipo === 'entrada')
    .reduce((acc, m) => acc + Number(m.valor || 0), 0);

  const totalSaidasPagina = movimentacoes
    .filter(m => m.tipo === 'saida')
    .reduce((acc, m) => acc + Number(m.valor || 0), 0);

  const headerGradient =
    theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(theme.palette.success.dark, 0.35)} 0%, ${alpha(theme.palette.primary.main, 0.25)} 50%, ${alpha('#1a1a2e', 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha('#e8f5e9', 0.95)} 0%, ${alpha(theme.palette.primary.light, 0.15)} 45%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`;

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          p: { xs: 1.25, md: 1.5 },
          borderRadius: 2,
          background: headerGradient,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <AccountBalanceIcon sx={{ fontSize: 120, color: theme.palette.success.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
            <IconButton onClick={handleGoBack} size="small" sx={{ bgcolor: alpha(theme.palette.background.paper, 0.6), border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.35)}`,
              }}
            >
              <AccountBalanceIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2} noWrap title={caixa.nome}>{caixa.nome}</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" useFlexGap mt={0.25}>
                <Chip label={caixa.status === 'aberto' ? 'Aberto' : 'Fechado'} color={caixa.status === 'aberto' ? 'success' : 'error'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                <Chip label={caixa.ativo ? 'Ativo' : 'Inativo'} color={caixa.ativo ? 'success' : 'default'} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                {caixa.is_default && <Chip label="Default" color="primary" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />}
                <Typography variant="caption" color="text.secondary">Criado em {formatarDataHora(caixa.createdAt)}</Typography>
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => { loadResumo(); loadMovimentacoes(); }}>Atualizar</Button>
            <Button variant="contained" size="small" startIcon={<EditIcon />} onClick={() => setEditModal(true)}>Editar</Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={1.25} mb={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Saldo Atual"
            valor={formatarValor(caixa.saldo_atual)}
            subtituloLabel="Inicial:"
            subtituloValor={formatarValor(caixa.saldo_inicial)}
            icone={<AttachMoneyIcon />}
            cor={theme.palette.primary.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Entradas"
            badge="Mês"
            valor={formatarValor(stats.total_entradas_mes)}
            subtituloLabel="Filtrado:"
            subtituloValor={formatarValor(totaisFiltrados.total_entradas)}
            icone={<TrendingUpIcon />}
            cor={theme.palette.success.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Saídas"
            badge="Mês"
            valor={formatarValor(stats.total_saidas_mes)}
            subtituloLabel="Filtrado:"
            subtituloValor={formatarValor(totaisFiltrados.total_saidas)}
            icone={<TrendingDownIcon />}
            cor={theme.palette.error.main}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            titulo="Movimentações"
            badge="Mês"
            valor={stats.quantidade_movimentacoes_mes}
            subtituloLabel="Filtrado:"
            subtituloValor={`${pagination.total} movimentações`}
            icone={<TimelineIcon />}
            cor={theme.palette.info.main}
          />
        </Grid>
      </Grid>

      {/* Caixa de Filtros com Collapse idêntica à de PreCadastrosPage */}
      <Paper
        elevation={0}
        sx={{
          mb: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box sx={filterBarHeaderSx(theme)} onClick={() => setShowFilters(!showFilters)}>
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filtros
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={`${pagination.total} resultado(s)`}
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <IconButton size="small">
            {showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={showFilters}>
          <Box sx={{ p: 1 }}>
            <Grid container spacing={1} alignItems="center">
              {/* Tipo de Movimentação */}
              <Grid item xs={12} sm={6} md={2.4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={filters.tipo || 'todos'}
                    label="Tipo"
                    onChange={(e) => handleFilterChange('tipo', e.target.value)}
                    sx={{ borderRadius: 2 }}
                    size="small"
                  >
                    <MenuItem value="todos">Todos os Tipos</MenuItem>
                    <MenuItem value="entrada">Entradas (+)</MenuItem>
                    <MenuItem value="saida">Saídas (-)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Forma de Pagamento */}
              <Grid item xs={12} sm={6} md={2.4}>
                <FormaPgtoSelect
                  value={filters.forma_pagamento || ''}
                  onChange={(val) => handleFilterChange('forma_pagamento', val)}
                  size="small"
                  allowClear
                />
              </Grid>

              {/* Gateway */}
              <Grid item xs={12} sm={6} md={2.4}>
                <GatewaySelect
                  value={
                    filters.gateway_id === GATEWAY_FILTER_SEM_VALUE
                      ? GATEWAY_FILTER_SEM_VALUE
                      : filters.gateway_id !== undefined && filters.gateway_id !== ''
                        ? Number(filters.gateway_id)
                        : ''
                  }
                  onChange={(gatewayId) =>
                    handleFilterChange('gateway_id', gatewayId != null ? String(gatewayId) : '')
                  }
                  onSemGatewayFilter={() =>
                    handleFilterChange('gateway_id', GATEWAY_FILTER_SEM_VALUE)
                  }
                  label="Gateway"
                  size="small"
                  showOnlyActive={false}
                  required={false}
                  includeSemGatewayOption
                />
              </Grid>

              {/* Coletor */}
              <Grid item xs={12} sm={6} md={2.4}>
                <UsuariosSelect
                  value={filters.coletor_id ? Number(filters.coletor_id) : null}
                  onChange={(val) => handleFilterChange('coletor_id', val != null ? String(val) : '')}
                  label="Coletor"
                  placeholder="Todos os coletores"
                  size="small"
                  allowClear
                  sx={{ borderRadius: 2 }}
                />
              </Grid>

              {/* Período */}
              <Grid item xs={12} sm={6} md={2.4}>
                <SysDateRangePicker
                  label="Período"
                  value={{
                    startDate: parseYmdToLocalDate(filters.data_inicio),
                    endDate: parseYmdToLocalDate(filters.data_fim),
                  }}
                  onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
                    const startStr = dateToYmd(range.startDate);
                    const endStr = dateToYmd(range.endDate);
                    handleFilterChange('data_inicio', startStr || undefined);
                    handleFilterChange('data_fim', endStr || undefined);
                  }}
                />
              </Grid>

              {hasActiveFilters && (
                <Grid item xs={12} sm={2} md={1.5}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={handleClearFilters}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    Limpar
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        </Collapse>

        {loadingMovimentacoes && <LinearProgress sx={{ borderRadius: 0 }} />}
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: alpha(theme.palette.primary.main, 0.04), borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" fontWeight={700}>Histórico de Movimentações</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip size="small" label={`${pagination.total} registro${pagination.total !== 1 ? 's' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
            <IconButton size="small" onClick={loadMovimentacoes} disabled={loadingMovimentacoes} title="Atualizar movimentações">
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {loadingMovimentacoes && movimentacoes.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando movimentações...</Typography>
          </Box>
        ) : movimentacoes.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <TimelineIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Nenhuma movimentação encontrada</Typography>
            <Typography variant="caption" color="text.secondary">Ajuste os filtros ou aguarde novos registros.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.06),
                      '& .MuiTableCell-head': {
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: 'text.secondary',
                        py: 0.75,
                        px: 1,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      },
                    }}
                  >
                    <TableCell>Data</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Forma Pagamento</TableCell>
                    <TableCell align="right">Entrada</TableCell>
                    <TableCell align="right">Saída</TableCell>
                    <TableCell>Coletor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movimentacoes.map((movimentacao) => (
                    <TableRow key={movimentacao.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                      <TableCell>
                        <Typography variant="caption">{formatarDataHora(movimentacao.data_registro)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={600}>{movimentacao.descricao}</Typography>
                        {movimentacao.categoria && (
                          <Typography variant="caption" color="text.secondary" display="block">{movimentacao.categoria}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {movimentacao.cliente ? (
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{
                              color: 'primary.main',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={() => navigate(`/clients/${movimentacao.cliente?.id}`)}
                            title={`Ver cliente: ${movimentacao.cliente.nome}`}
                          >
                            {movimentacao.cliente.nome}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={600}>
                          {getFormaPagamentoNome(movimentacao.forma_pagamento)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {movimentacao.tipo === 'entrada' ? (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                            <Typography variant="caption" fontWeight={700} color="success.main">
                              {formatarValor(movimentacao.valor)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {movimentacao.tipo === 'saida' ? (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                            <Typography variant="caption" fontWeight={700} color="error.main">
                              {formatarValor(movimentacao.valor)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={600} display="block">
                          {movimentacao.coletor_nome}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem' }}>
                          {movimentacao.coletor_tipo === 'webhook'
                            ? 'Webhook / Gateway'
                            : 'Usuário'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                {!loadingMovimentacoes && movimentacoes.length > 0 && (
                  <TableFooter sx={{ position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper' }}>
                    <TableRow
                      sx={{
                        '& .MuiTableCell-root': {
                          bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                          fontWeight: 800,
                          fontSize: '0.78rem',
                          borderTop: `2px solid ${alpha(primario, 0.25)}`,
                          py: 1,
                          px: 1,
                          color: 'text.primary',
                        },
                      }}
                    >
                      <TableCell colSpan={4} align="right" sx={{ letterSpacing: '0.05em' }}>
                        TOTAL DA PÁGINA
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          <Typography variant="caption" fontWeight={800} color="success.main">
                            {formatarValor(totalEntradasPagina)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
                          <Typography variant="caption" fontWeight={800} color="error.main">
                            {formatarValor(totalSaidasPagina)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
            {pagination.total > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 0.8,
                  px: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                }}
              >
                <TablePagination
                  component="div"
                  count={pagination.total}
                  page={Math.max(0, (filters.page || 1) - 1)}
                  onPageChange={(_, newPage) => {
                    setFilters(prev => ({ ...prev, page: newPage + 1 }));
                  }}
                  rowsPerPage={filters.limit || 20}
                  onRowsPerPageChange={(e) => {
                    const newRowsPerPage = parseInt(e.target.value, 10);
                    setFilters(prev => ({ ...prev, limit: newRowsPerPage, page: 1 }));
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  labelRowsPerPage="Linhas por pág:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                  }
                  sx={{
                    width: '100%',
                    '.MuiTablePagination-toolbar': {
                      minHeight: 32,
                      flexWrap: 'nowrap',
                      justifyContent: 'center',
                      gap: 1,
                      p: 0,
                      '& .MuiTablePagination-spacer': { display: 'none' },
                    },
                    '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
                    '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
                    '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
                    '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Modal de Edição */}
      <Dialog
        open={editModal}
        onClose={() => setEditModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar Caixa</DialogTitle>
        <DialogContent>
          <CaixaFinanceiroForm
            caixa={caixa}
            onSuccess={handleEditSuccess}
            onCancel={() => setEditModal(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CaixaFinanceiroDetailsPage;
